import RefreshIcon from '@mui/icons-material/Refresh'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Icon from '@mui/material/Icon'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import React from 'react'

import Button from '@/app/component/atom/button'
import IconButton from '@/app/component/atom/icon-button'
import TabHeader from '@/app/component/atom/tab-header'
import TablePagination from '@/app/component/atom/table-pagination'
import { RECORDINGS_EXPORT_LIMIT, type RecordingsDateRange } from '@/app/module/reports/call-recordings-query'
import { useDownloadCallRecording } from '@/app/module/reports/hooks/use-download-call-recording'
import { useExportCallRecordings } from '@/app/module/reports/hooks/use-export-call-recordings'
import { useGetCallRecordings } from '@/app/module/reports/hooks/use-get-call-recordings'
import { numberWithCommas, pluralize } from '@/app/service/util/format'

import CallRecordingsFilters from './call-recordings-filters'
import CallRecordingsTable from './call-recordings-table'

type Query = {
  page: number
  size: number
  campid?: number
  dates?: RecordingsDateRange
  userid?: number
}

const pageSizeValues = [
  { label: '10', value: 10 },
  { label: '25', value: 25 },
  { label: '50', value: 50 },
  { label: '100', value: 100 },
]

const CallRecordingsListContainer: React.FC = () => {
  const [query, setQuery] = React.useState<Query>({ page: 1, size: 25 })
  const filters = {
    campid: query.campid,
    userid: query.userid,
    startDate: query.dates?.from,
    endDate: query.dates?.to,
  }
  const { currentData, data, isError, isFetching, isLoading, refetch } = useGetCallRecordings({
    ...filters,
    page: query.page,
    size: query.size,
  })
  const [downloadRecording] = useDownloadCallRecording()
  const [exportRecordings, { isLoading: exporting }] = useExportCallRecordings()
  const [downloadingId, setDownloadingId] = React.useState<number>()

  const handleDownloadRecording = React.useCallback(
    async (id: number) => {
      setDownloadingId(id)
      try {
        await downloadRecording(id)
      } finally {
        setDownloadingId(undefined)
      }
    },
    [downloadRecording],
  )

  const total = data?.total || 0
  const recordings = data?.recordings || []
  const loading = isFetching || isLoading
  const downloadCount = Math.min(total, RECORDINGS_EXPORT_LIMIT)

  return (
    <Container>
      <TabHeader
        action={
          <Toolbar>
            <CallRecordingsFilters
              campid={query.campid}
              dates={query.dates}
              disabled={loading || exporting}
              userid={query.userid}
              onChange={(changes) => setQuery((s) => ({ ...s, ...changes, page: 1 }))}
            />
            <Button
              disabled={loading || exporting || isError || !currentData || !downloadCount}
              startIcon={<Icon>cloud_download</Icon>}
              variant="contained"
              onClick={() => exportRecordings(filters)}
            >
              {exporting
                ? 'Preparing export…'
                : `Download ${numberWithCommas(downloadCount)} ${pluralize(downloadCount, 'recording', 'recordings')}`}
            </Button>
            <IconButton
              aria-label="Refresh recordings"
              data-testid="recordings-refresh"
              disabled={loading || exporting}
              size="small"
              tooltip="Refresh"
              onClick={() => refetch()}
            >
              <RefreshIcon />
            </IconButton>
          </Toolbar>
        }
        title="Call Recordings"
      />
      <Content>
        {total > RECORDINGS_EXPORT_LIMIT && (
          <Typography variant="body2">
            Only {numberWithCommas(RECORDINGS_EXPORT_LIMIT)} of {numberWithCommas(total)} matching recordings will be
            included. Narrow your filters to download a specific set.
          </Typography>
        )}
        <Scrollable>
          <CallRecordingsTable
            data={recordings}
            downloadingId={downloadingId}
            loading={loading}
            onDownload={handleDownloadRecording}
          />
        </Scrollable>
        {!!total && (
          <TablePagination
            namespace="recordings"
            page={query.page}
            pageSizeValues={pageSizeValues}
            size={query.size}
            total={total}
            onPageChange={(page) => setQuery((s) => ({ ...s, page }))}
            onPageSizeChange={(size) => setQuery((s) => ({ ...s, size, page: 1 }))}
          />
        )}
      </Content>
    </Container>
  )
}

const Container = styled(Card)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: 0,
  overflow: 'hidden',
})

const Toolbar = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: theme.spacing(1.5),
}))

const Content = styled(CardContent)({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  flex: 1,
  minHeight: 0, // allow the inner flex child to shrink and enable scrolling
  overflow: 'hidden',
})

const Scrollable = styled('div')({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  overflow: 'hidden',
})

export default CallRecordingsListContainer
